import type React from 'react';

const DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;

interface Interval { day: string; start: string; end: string; }

function hhmmToMin(s: string): number {
  const [h, m] = s.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}

// Visualises a weekly pattern as a 7-row × 24-hour horizontal grid.
// A midnight-wrap interval (start > end) is rendered as two segments.
export function WeeklyGrid({ intervals }: { intervals: readonly Interval[] }) {
  return (
    <div role="img" aria-label="Weekly schedule" style={{
      display: 'grid', gridTemplateColumns: '32px 1fr', gap: 'var(--sp-1)', fontSize: 'var(--fs-xs)',
    }}>
      {DAYS.map((day) => {
        const dayIvs = intervals.filter((iv) => iv.day === day);
        return (
          <FragmentFor key={day} label={day}>
            <div style={{ position: 'relative', height: 16, background: 'var(--border)', borderRadius: 3 }}>
              {dayIvs.map((iv, i) => {
                const s = hhmmToMin(iv.start), e = hhmmToMin(iv.end);
                const segments: ReadonlyArray<readonly [number, number]> = e > s
                  ? [[s, e] as const]
                  : [[s, 1440] as const, [0, e] as const];
                return segments.map(([from, to], j) => (
                  <div
                    key={`${i}-${j}`}
                    style={{
                      position: 'absolute',
                      left:  `${(from / 1440) * 100}%`,
                      width: `${((to - from) / 1440) * 100}%`,
                      top: 0, bottom: 0,
                      background: 'var(--accent)',
                      borderRadius: 3,
                    }}
                    title={`${iv.start}–${iv.end}`}
                  />
                ));
              })}
            </div>
          </FragmentFor>
        );
      })}
    </div>
  );
}

function FragmentFor({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <div className="muted" style={{ textTransform: 'uppercase' }}>{label}</div>
      {children}
    </>
  );
}
